import {
	skipToken,
	useInfiniteQuery,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { useMemo } from "react";
import { type CampListQuery, campApi } from "../../camp-api";
import type { RosterFilter } from "../../camp-api/csv";
import type {
	AddMember,
	CampId,
	CampStatus,
	CreateCamp,
	CreateOrganizer,
	EditionBody,
	EditionId,
	OrganizerId,
	OrganizerProfile,
	RegistrationId,
	SettlementKind,
	StaffRegistration,
	StaffRole,
	UpdateCamp,
	UserId,
} from "../../schemas/camp";
import { queryKeys } from "../query-keys";
import { flattenPages, nextPageParam } from "../venue/pagination";
import { keepWithin, retryServerErrors } from "./query-options";
import { useReadToEnd } from "./use-read-to-end";

const keys = queryKeys.campPortal;

/** Server pages: the roster is the long list (<= 2,000 rows at 100x), the others stay short. */
export const ROSTER_PAGE_SIZE = 50;
export const CAMPS_PAGE_SIZE = 50;
export const EDITIONS_PAGE_SIZE = 50;
/** Memberships and the team are tens of rows: ask for the biggest page and read on to the end. */
export const SHORT_LIST_PAGE_SIZE = 100;

// Reads take `Id | null` (nothing chosen yet) and use `skipToken`, so a fetch can never run
// without its ids and no `!` assertion is needed.

export function useMyOrganizers() {
	const query = useInfiniteQuery({
		getNextPageParam: nextPageParam,
		initialPageParam: undefined as string | undefined,
		queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
			campApi.organizers.list({ cursor: pageParam, limit: SHORT_LIST_PAGE_SIZE }),
		queryKey: keys.organizers(),
		retry: retryServerErrors,
	});
	useReadToEnd(query);
	// Memoised: the active-organizer context depends on this list's identity.
	const memberships = useMemo(() => flattenPages(query.data?.pages), [query.data]);
	return { ...query, memberships };
}

/** Owner-only on the server; a manager's or staff member's call is a 403. */
export function useOrganizerMembers(organizerId: OrganizerId | null) {
	const query = useInfiniteQuery({
		getNextPageParam: nextPageParam,
		initialPageParam: undefined as string | undefined,
		placeholderData: keepWithin(organizerId),
		queryFn: organizerId
			? ({ pageParam }: { pageParam: string | undefined }) =>
					campApi.members.list(organizerId, { cursor: pageParam, limit: SHORT_LIST_PAGE_SIZE })
			: skipToken,
		queryKey: keys.members(organizerId ?? ""),
		retry: retryServerErrors,
	});
	useReadToEnd(query);
	const members = useMemo(() => flattenPages(query.data?.pages), [query.data]);
	return { ...query, members };
}

export function useOrganizerCamps(organizerId: OrganizerId | null, status?: CampStatus) {
	const query = useInfiniteQuery({
		getNextPageParam: nextPageParam,
		initialPageParam: undefined as string | undefined,
		// The status filter keeps the rows up; another organizer's never stand in.
		placeholderData: keepWithin(organizerId),
		queryFn: organizerId
			? ({ pageParam }: { pageParam: string | undefined }) => {
					const request: CampListQuery = { cursor: pageParam, limit: CAMPS_PAGE_SIZE };
					if (status !== undefined) request.status = status;
					return campApi.camps.list(organizerId, request);
				}
			: skipToken,
		queryKey: keys.camps(organizerId ?? "", status),
		retry: retryServerErrors,
	});
	return { ...query, camps: flattenPages(query.data?.pages) };
}

export function useCamp(organizerId: OrganizerId | null, campId: CampId | null) {
	return useQuery({
		placeholderData: keepWithin(organizerId, campId),
		queryFn: campId ? () => campApi.camps.get(campId) : skipToken,
		queryKey: keys.camp(organizerId ?? "", campId ?? ""),
		retry: retryServerErrors,
	});
}

export function useCampEditions(organizerId: OrganizerId | null, campId: CampId | null) {
	const query = useInfiniteQuery({
		getNextPageParam: nextPageParam,
		initialPageParam: undefined as string | undefined,
		placeholderData: keepWithin(organizerId, campId),
		queryFn: campId
			? ({ pageParam }: { pageParam: string | undefined }) =>
					campApi.editions.list(campId, { cursor: pageParam, limit: EDITIONS_PAGE_SIZE })
			: skipToken,
		queryKey: keys.editions(organizerId ?? "", campId ?? ""),
		retry: retryServerErrors,
	});
	return { ...query, editions: flattenPages(query.data?.pages) };
}

export function useEdition(organizerId: OrganizerId | null, editionId: EditionId | null) {
	return useQuery({
		placeholderData: keepWithin(organizerId, editionId),
		queryFn: editionId ? () => campApi.editions.get(editionId) : skipToken,
		queryKey: keys.edition(organizerId ?? "", editionId ?? ""),
		retry: retryServerErrors,
	});
}

/** The roster, paged by the server; the state filter and the search `q` are request parameters. */
export function useEditionRoster(
	organizerId: OrganizerId | null,
	editionId: EditionId | null,
	filter: RosterFilter,
) {
	const query = useInfiniteQuery({
		getNextPageParam: nextPageParam,
		initialPageParam: undefined as string | undefined,
		// Typing in the search box keeps the rows on screen until the new ones arrive.
		placeholderData: keepWithin(organizerId, editionId),
		queryFn:
			organizerId && editionId
				? ({ pageParam }: { pageParam: string | undefined }) =>
						campApi.roster.list(organizerId, editionId, {
							...filter,
							cursor: pageParam,
							limit: ROSTER_PAGE_SIZE,
						})
				: skipToken,
		queryKey: keys.roster(organizerId ?? "", editionId ?? "", filter),
		retry: retryServerErrors,
	});
	return { ...query, registrations: flattenPages(query.data?.pages) };
}

/** One-off read of the whole roster as a file for a download; nothing is cached or kept. */
export const useExportRosterCsv = (organizerId: OrganizerId, editionId: EditionId) =>
	useMutation({
		mutationFn: (filter: RosterFilter) => campApi.roster.exportCsv(organizerId, editionId, filter),
	});

type KeyOf = readonly unknown[];

/** Each mutation invalidates exactly the slices it changes, plus the organizer's camp lists. */
function useOrganizerMutation<TVariables, TResult>(
	organizerId: OrganizerId,
	mutationFn: (variables: TVariables) => Promise<TResult>,
	slices: readonly KeyOf[],
) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn,
		onSuccess: () => Promise.all(slices.map((queryKey) => qc.invalidateQueries({ queryKey }))),
	});
}

export function useCreateOrganizer() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (body: CreateOrganizer) => campApi.organizers.create(body),
		onSuccess: () => qc.invalidateQueries({ queryKey: keys.organizers() }),
	});
}

export const useUpdateOrganizerProfile = (organizerId: OrganizerId) =>
	useOrganizerMutation(
		organizerId,
		(body: OrganizerProfile) => campApi.organizers.updateProfile(organizerId, body),
		[keys.organizers()],
	);

export const usePublishOrganizer = (organizerId: OrganizerId) =>
	useOrganizerMutation(organizerId, () => campApi.organizers.publish(organizerId), [
		keys.organizers(),
	]);

export const useAddOrganizerMember = (organizerId: OrganizerId) =>
	useOrganizerMutation(organizerId, (body: AddMember) => campApi.members.add(organizerId, body), [
		keys.members(organizerId),
	]);

export const useChangeOrganizerMemberRole = (organizerId: OrganizerId) =>
	useOrganizerMutation(
		organizerId,
		({ userId, role }: { userId: UserId; role: StaffRole }) =>
			campApi.members.changeRole(organizerId, userId, role),
		[keys.members(organizerId), keys.organizers()],
	);

export const useRemoveOrganizerMember = (organizerId: OrganizerId) =>
	useOrganizerMutation(
		organizerId,
		(userId: UserId) => campApi.members.remove(organizerId, userId),
		[keys.members(organizerId), keys.organizers()],
	);

export const useCreateOrganizerCamp = (organizerId: OrganizerId) =>
	useOrganizerMutation(organizerId, (body: CreateCamp) => campApi.camps.create(organizerId, body), [
		keys.campLists(organizerId),
	]);

export const useUpdateOrganizerCamp = (organizerId: OrganizerId, campId: CampId) =>
	useOrganizerMutation(organizerId, (body: UpdateCamp) => campApi.camps.update(campId, body), [
		keys.camp(organizerId, campId),
		keys.campLists(organizerId),
	]);

export const usePublishCamp = (organizerId: OrganizerId, campId: CampId) =>
	useOrganizerMutation(organizerId, () => campApi.camps.publish(campId), [
		keys.camp(organizerId, campId),
		keys.campLists(organizerId),
	]);

export const useArchiveCamp = (organizerId: OrganizerId, campId: CampId) =>
	useOrganizerMutation(organizerId, () => campApi.camps.archive(campId), [
		keys.camp(organizerId, campId),
		keys.campLists(organizerId),
	]);

export const useCreateEdition = (organizerId: OrganizerId, campId: CampId) =>
	useOrganizerMutation(organizerId, (body: EditionBody) => campApi.editions.create(campId, body), [
		keys.editions(organizerId, campId),
		keys.campLists(organizerId),
	]);

/**
 * Edit, open, close and cancel change the edition row, its camp's list and the camp counts. Each can
 * also move registrations (a cancel, a promoted waitlist), so the roster is stale too.
 */
function useEditionMutation<TVariables, TResult>(
	organizerId: OrganizerId,
	campId: CampId,
	editionId: EditionId,
	mutationFn: (variables: TVariables) => Promise<TResult>,
	extra: readonly KeyOf[] = [],
) {
	return useOrganizerMutation(organizerId, mutationFn, [
		keys.edition(organizerId, editionId),
		keys.editions(organizerId, campId),
		keys.campLists(organizerId),
		keys.rosters(organizerId, editionId),
		...extra,
	]);
}

export const useUpdateEdition = (organizerId: OrganizerId, campId: CampId, editionId: EditionId) =>
	useEditionMutation(organizerId, campId, editionId, (body: EditionBody) =>
		campApi.editions.update(editionId, body),
	);

export const useOpenEdition = (organizerId: OrganizerId, campId: CampId, editionId: EditionId) =>
	useEditionMutation(organizerId, campId, editionId, () => campApi.editions.open(editionId));

export const useCloseEdition = (organizerId: OrganizerId, campId: CampId, editionId: EditionId) =>
	useEditionMutation(organizerId, campId, editionId, () => campApi.editions.close(editionId));

export const useCancelEdition = (organizerId: OrganizerId, campId: CampId, editionId: EditionId) =>
	useEditionMutation(organizerId, campId, editionId, () => campApi.editions.cancel(editionId));

/** A roster change moves seats: the roster, the edition's `taken` and the camp counts all refresh. */
function useRosterMutation<TVariables>(
	organizerId: OrganizerId,
	campId: CampId,
	editionId: EditionId,
	mutationFn: (variables: TVariables) => Promise<unknown>,
) {
	return useOrganizerMutation(organizerId, mutationFn, [
		keys.rosters(organizerId, editionId),
		keys.edition(organizerId, editionId),
		keys.editions(organizerId, campId),
		keys.campLists(organizerId),
	]);
}

export const useAddToRoster = (organizerId: OrganizerId, campId: CampId, editionId: EditionId) =>
	useRosterMutation(organizerId, campId, editionId, (body: StaffRegistration) =>
		campApi.roster.add(organizerId, editionId, body),
	);

export const useCancelRegistration = (
	organizerId: OrganizerId,
	campId: CampId,
	editionId: EditionId,
) =>
	useRosterMutation(
		organizerId,
		campId,
		editionId,
		({ registrationId, reason }: { registrationId: RegistrationId; reason?: string }) =>
			campApi.roster.cancel(organizerId, editionId, registrationId, reason),
	);

export const useSetSettlement = (organizerId: OrganizerId, campId: CampId, editionId: EditionId) =>
	useRosterMutation(
		organizerId,
		campId,
		editionId,
		({ registrationId, kind }: { registrationId: RegistrationId; kind: SettlementKind }) =>
			campApi.roster.setSettlement(organizerId, editionId, registrationId, kind),
	);
